import { plansDetails } from '@plura/shared/utils';
import { ReactNode } from 'react';
import Stripe from 'stripe';
import { z } from "zod";

const timestampToDate = z.preprocess((val) => {
  if (typeof val === 'number') {
    return new Date(val * 1000); // Convert seconds to milliseconds and then to Date
  }
  return val;
}, z.date());

export const CheckoutSession = z.object({
  id: z.string(),
  object: z.string(),
  total: z.number().nullable(),
  createdAt: z.date(),
  currency: z.string().nullable(),
  mode: z.string(),
  paymentStatus: z.string(),
  status: z.string().nullable(),
})

export type CheckoutSession = z.infer<typeof CheckoutSession>;

export const Price = z.object({
  id: z.string(),
  name: z.string(),
  classNames: z.string().optional(),
  active: z.boolean(),
  nickname: z.string().nullable(),
  amount: z.number().nullable(),
  product: z.string(),
  featureList: z.array(z.object({
    id: z.string(),
    title: z.string(),
    description: z.string().optional(),
    icon: z.string().optional()
  })),
  flag: z.string().nullable(),
  caption: z.string().optional(),
  recurring: z.object({
    aggregateUsage: z.string().nullable(),
    interval: z.string(),
    intervalCount: z.number().nullable(),
    trialPeriodDays: z.number().nullable(),
    usageType: z.string().nullable(),
  }),
  tiers: z.array(
    z.object({
      amount: z.number(),
      upTo: z.number(),
    })
  ).nullish()
})

export type Price = z.infer<typeof Price>;

export type PriceWithProduct = Stripe.Price & { product: Stripe.Product & { tiers?: Stripe.Price.Tier[] } };

export const StripePlan = z.object({
  id: z.string(),
  object: z.string(),
  active: z.boolean(),
  amount: z.number().nullable(),
  quantity: z.number().nullish(),
  amount_decimal: z.string().nullable(),
  created: timestampToDate,
  interval: z.string(),
  nickname: z.string().nullable(),
  product: z.string(),
})

export type StripePlan = z.infer<typeof StripePlan>;

export const Invoice = z.object({
  id: z.string(),
  amount_paid: z.number(),
  created: timestampToDate,
  hosted_invoice_url: z.string().nullable(),
  number: z.string().nullable(),
  period_end: timestampToDate,
  total: z.number(),
})

export type Invoice = z.infer<typeof Invoice>;


export const SubscriptionDiscount = z.object({
  // Stripe coupon id (e.g. "abc123") — useful as a stable key.
  couponId: z.string(),
  // Human-readable label, falls back to "X% off" / "$X off" when the
  // underlying coupon doesn't have a `name`.
  label: z.string(),
  // Either percentOff OR amountOff will be populated, mirroring Stripe.
  percentOff: z.number().nullable(),
  amountOff: z.number().nullable(),
  currency: z.string().nullable(),
  // Stripe coupon duration: "once" | "repeating" | "forever".
  duration: z.string(),
  durationInMonths: z.number().nullable(),
  // For "repeating" coupons: when the discount stops applying. Null for
  // "once" / "forever".
  endsAt: z.date().nullable(),
});

export type SubscriptionDiscount = z.infer<typeof SubscriptionDiscount>;

export const Subscription = z.object({
  id: z.string(),
  created: timestampToDate,
  current_period_start: timestampToDate,
  current_period_end: timestampToDate,
  cancel_at_period_end: z.boolean(),
  quantity: z.number().nullable(),
  usedConversations: z.number(),
  plan: StripePlan,
  invoices: z.array(Invoice),
  items: z.array(z.object({
    id: z.string(),
  })).nullable(),
  // The active coupon/promotion code applied to this subscription, if any.
  // Populated by `getWorkspaceBillingDetails` from `subscription.discounts[0]`.
  discount: SubscriptionDiscount.nullable().optional(),
})

export type Subscription = z.infer<typeof Subscription>;


// Mappers
export const mapStripePriceQueryToPrice = (input: PriceWithProduct): Price => {
  if (!input.recurring?.interval) {
    throw new Error('Stripe interval data is missing')
  }

  let interval = '';
  switch (input.recurring.interval) {
    case 'year': {
      interval = 'annually'
      break;
    }
    case 'month': {
      interval = 'monthly'
      break;
    }
  }

  const featureList: {
    id: string;
    title: string;
    description?: string;
    icon?: string;
  }[] = plansDetails.find(p => p.title === input.metadata.title)?.features.card ?? [];

  return {
    id: input.id,
    name: input.metadata.title,
    classNames: "title-purple",
    active: input.active,
    nickname: input.metadata.title,
    amount: input.unit_amount,
    caption: input.metadata.caption,
    flag: input.metadata.flag ? input.metadata.flag : null,
    featureList: featureList,
    product: input.product.id,
    recurring: {
      aggregateUsage: input.recurring?.aggregate_usage ?? null,
      interval: interval,
      intervalCount: input.recurring?.interval_count ?? null,
      trialPeriodDays: input.recurring?.trial_period_days ?? null,
      usageType: input.recurring?.usage_type ?? null,
    },
    tiers: input.tiers
      ? input.tiers.map(tier => ({
        amount: +`${tier.unit_amount_decimal}`,
        upTo: tier.up_to ?? 5000,
      }))
      : null,
  }
}